import { cn } from '@/lib/utils';
import { typographyVariants } from '@/components/ui/typography';
import type { PMSVendorData } from '@/types/pms.types';
import { convertToPasswordFormat, maskKey } from '../utils/maskKey';
import { integrationStatusCopy as copy } from '../vendor-settings/status/copy';

export interface SummaryRow {
  label: string;
  value: string;
}

/**
 * Which credential shape a vendor stores (spec §5.3).
 *
 * `keySecretOnly` exists for legacy AppFolio admin records: that form never
 * collected a subdomain, yet seeds `url: 'api'`, so the persisted placeholder
 * would otherwise surface as a bogus "AppFolio subdomain: api" row.
 */
export type CredentialSummaryKind =
  | 'keySecret'
  | 'keySecretOnly'
  | 'subdomainOnly'
  | 'marketplace';

/**
 * Rows for the read-only summary. Key shows its last four characters, secret
 * shows a fixed-width mask; for the two subdomain-bearing kinds that row is
 * omitted when nothing is stored for it.
 *
 * Never returns an empty list. `subdomainOnly` (AppFolio OAuth) has exactly one
 * row to drop, and the OAuth view renders nothing at all in read mode, so a
 * record persisted without a subdomain would otherwise leave the page showing a
 * header, a divider and an empty `dl` (review finding 5). A single explicit
 * "nothing stored" row says that instead.
 */
export const credentialRows = (
  kind: CredentialSummaryKind,
  vendorName: string,
  record: Pick<PMSVendorData, 'url' | 'key' | 'secret'>,
): SummaryRow[] => {
  if (kind === 'marketplace') {
    return [{ label: copy.credentials.connection, value: copy.credentials.marketplace(vendorName) }];
  }
  const rows: SummaryRow[] = [];
  if (kind !== 'keySecretOnly' && record.url?.trim()) {
    rows.push({ label: copy.credentials.subdomain(vendorName), value: record.url });
  }
  if (kind === 'keySecret' || kind === 'keySecretOnly') {
    rows.push({ label: copy.credentials.apiKey, value: maskKey(record.key ?? '') });
    rows.push({ label: copy.credentials.apiSecret, value: convertToPasswordFormat(record.secret ?? '') });
  }
  if (rows.length === 0) {
    return [{ label: copy.credentials.connection, value: copy.credentials.none }];
  }
  return rows;
};

interface Props {
  vendorName: string;
  rows: SummaryRow[];
  className?: string;
}

/**
 * Read-only credential grid for the vendor settings page (spec §5.3):
 * 170 px label column, muted labels, medium-weight values. Never receives
 * raw secrets — callers pass `credentialRows()` output.
 */
export const CredentialSummary = ({ vendorName, rows, className }: Props) => (
  // The list roles are explicit on purpose: HTML-AAM gives `dl` no implicit
  // list role, and each row's `display: contents` (needed for the grid) drops
  // its own box, so the items need `listitem` spelled out to stay announced.
  <dl
    role="list"
    aria-label={`${vendorName} credentials`}
    className={cn('grid grid-cols-[170px_1fr] gap-x-10 gap-y-3 max-sm:grid-cols-1 max-sm:gap-y-1', className)}
  >
    {rows.map((row) => (
      <div key={row.label} role="listitem" className="contents">
        <dt className={cn(typographyVariants({ variant: 'body' }), 'text-muted-foreground')}>{row.label}</dt>
        <dd className={cn(typographyVariants({ variant: 'body-medium' }), 'm-0 break-all max-sm:mb-2')}>{row.value}</dd>
      </div>
    ))}
  </dl>
);
